import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { policyService } from '../services/policy'
import type { RenewalPipelineLead } from '../../../types'
import { CalendarClock, RefreshCw, ChevronDown } from 'lucide-react'
import { useWindowWidth } from '../../../lib/useWindowWidth'

const DAYS_OPTIONS = [30, 60, 90] as const

function MobileRenewalCards({
  leads, daysUntilDue, urgencyColor, onNavigate,
}: {
  leads: RenewalPipelineLead[]
  daysUntilDue: (d: string) => number
  urgencyColor: (d: number) => string
  onNavigate: (id: number) => void
}) {
  const [expandedId, setExpandedId] = useState<number | null>(null)
  return (
    <div className="flex flex-col gap-2">
      {leads.map(lead => {
        const d = daysUntilDue(lead.insurance_due_date)
        const expanded = expandedId === lead.id
        return (
          <div key={lead.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              className="flex items-center gap-2.5 px-3 py-3 cursor-pointer"
              onClick={() => setExpandedId(expanded ? null : lead.id)}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{lead.customer_name}</p>
                <p className="text-xs tabular-nums" style={{ color: 'var(--text-tertiary)' }}>{lead.mobile}</p>
              </div>
              <span className="text-sm font-semibold shrink-0" style={{ color: urgencyColor(d) }}>
                {d <= 0 ? 'Overdue' : `${d}d`}
              </span>
              <ChevronDown
                className="h-4 w-4 shrink-0 transition-transform"
                style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }}
              />
            </div>
            {expanded && (
              <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                <div className="flex justify-between text-xs py-1">
                  <span style={{ color: 'var(--text-tertiary)' }}>Vehicle reg</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{lead.vehicle_reg_no || '—'}</span>
                </div>
                <div className="flex justify-between text-xs py-1">
                  <span style={{ color: 'var(--text-tertiary)' }}>Due date</span>
                  <span style={{ color: urgencyColor(d), fontWeight: 500 }}>
                    {new Date(lead.insurance_due_date).toLocaleDateString()}
                  </span>
                </div>
                {lead.policy_number && (
                  <div className="flex justify-between text-xs py-1">
                    <span style={{ color: 'var(--text-tertiary)' }}>Policy no.</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{lead.policy_number}</span>
                  </div>
                )}
                <button
                  className="w-full flex items-center justify-center gap-1.5 text-xs py-2 rounded-md border mt-3"
                  style={{ borderColor: 'var(--brand-primary)', color: 'white', background: 'var(--brand-primary)' }}
                  onClick={e => { e.stopPropagation(); onNavigate(lead.id) }}
                >
                  View Lead
                </button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function RenewalPipelinePage() {
  const navigate = useNavigate()
  const isMobile = useWindowWidth() < 768
  const [days, setDays] = useState<30 | 60 | 90>(30)
  const [data, setData] = useState<{ total: number; leads: RenewalPipelineLead[] } | null>(null)
  const [loading, setLoading] = useState(true)

  const load = async (d: number) => {
    setLoading(true)
    try {
      setData(await policyService.getRenewalPipeline(d))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load(days) }, [days])

  const daysUntilDue = (dateStr: string) => {
    const due = new Date(dateStr)
    const now = new Date()
    return Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  }

  const urgencyColor = (d: number): string => {
    if (d <= 7)  return 'var(--danger-text)'
    if (d <= 14) return 'var(--orange-text)'
    if (d <= 30) return 'var(--warning-text)'
    return 'var(--text-tertiary)'
  }

  return (
    <div className="space-y-6">
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <CalendarClock className="h-6 w-6" style={{ color: 'var(--brand-600)' }} />
            Renewal Pipeline
          </h1>
          <p className="page-subtitle">
            {data ? `${data.total} leads due within ${days} days` : 'Loading…'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Due within</span>
          {DAYS_OPTIONS.map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className="px-3 py-1.5 text-sm font-medium rounded-md border transition-colors"
              style={{
                borderColor: days === d ? 'var(--brand-primary)' : 'var(--border-default)',
                background: days === d ? 'var(--brand-primary)' : 'transparent',
                color: days === d ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {d}d
            </button>
          ))}
          <button
            onClick={() => load(days)}
            disabled={loading}
            className="btn-icon"
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {isMobile ? (
        loading ? (
          <div className="flex justify-center py-10"><div className="spinner" /></div>
        ) : !data || data.leads.length === 0 ? (
          <div className="empty-state">
            <p style={{ color: 'var(--text-tertiary)' }}>No renewals due within {days} days</p>
          </div>
        ) : (
          <MobileRenewalCards
            leads={data.leads}
            daysUntilDue={daysUntilDue}
            urgencyColor={urgencyColor}
            onNavigate={id => navigate(`/leads/${id}`)}
          />
        )
      ) : (
        <div className="table-card">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Vehicle Reg</th>
                  <th>Due Date</th>
                  <th>Days Left</th>
                  <th>Policy No.</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8" style={{ color: 'var(--text-tertiary)' }}>Loading…</td>
                  </tr>
                ) : !data || data.leads.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8" style={{ color: 'var(--text-tertiary)' }}>
                      No renewals due within {days} days
                    </td>
                  </tr>
                ) : (
                  data.leads.map(lead => {
                    const d = daysUntilDue(lead.insurance_due_date)
                    return (
                      <tr
                        key={lead.id}
                        className="clickable"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                      >
                        <td>
                          <p className="text-primary-data">{lead.customer_name}</p>
                          <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{lead.mobile}</p>
                        </td>
                        <td>{lead.vehicle_reg_no || '—'}</td>
                        <td>{new Date(lead.insurance_due_date).toLocaleDateString()}</td>
                        <td>
                          <span className="text-sm font-medium" style={{ color: urgencyColor(d) }}>
                            {d <= 0 ? 'Overdue' : `${d}d`}
                          </span>
                        </td>
                        <td style={{ color: 'var(--text-tertiary)' }}>{lead.policy_number || '—'}</td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
